import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHistory, FaInfoCircle } from 'react-icons/fa';
import { SMPSDevice } from '../utils/smpsTypes';
import { getStatusLabel, getBatteryModeLabel } from '../utils/smpsConstants';

interface SMPSDeviceTableProps {
  devices: SMPSDevice[];
  onDeviceClick: (device: SMPSDevice) => void;
}

const SMPSDeviceTable: React.FC<SMPSDeviceTableProps> = ({ devices, onDeviceClick }) => {
  const navigate = useNavigate();

  const handleViewDetails = (e: React.MouseEvent, device: SMPSDevice) => {
    e.stopPropagation();
    navigate(`/smps-device-detail?deviceId=${device.id}&deviceName=${device.name}`);
  };

  const handleViewHistory = (e: React.MouseEvent, device: SMPSDevice) => {
    e.stopPropagation();
    navigate(`/smps-device-history/${device.id}`);
  };

  const getRectifierStatus = (device: SMPSDevice) => {
    return `${device.rectifiers.online}/${device.rectifiers.total}`;
  };

  return (
    <div className="table-container">
      <div className="table-header">
        <div className="table-title">
          Power Supply Units Status <span>(Click any unit for quick view)</span>
        </div>
        <div className="table-count">Showing {devices.length} units</div>
      </div>
      <div className="table-scroll">
        <table className="devices-table">
          <thead>
            <tr>
              <th>Last Activity</th>
              <th>Device Name</th>
              <th>Station</th>
              <th>DC Voltage</th>
              <th>Load Current</th>
              <th>Battery</th>
              <th>Rectifiers</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id} onClick={() => onDeviceClick(device)}>
                <td className="last-activity">{device.lastActivity}</td>
                <td className="device-name-cell">{device.name}</td>
                <td>
                  <span className="station-badge">{device.station}</span>
                </td>
                <td>
                  <span className={`voltage-value ${device.dcVoltage === 0 ? 'offline' :
                    device.dcVoltage < 44 || device.dcVoltage > 54 ? 'critical' :
                      device.dcVoltage < 46 || device.dcVoltage > 52 ? 'warning' : 'normal'
                    }`}>
                    {device.dcVoltage > 0 ? `${device.dcVoltage}V` : '-'}
                  </span>
                </td>
                <td>
                  <span className="current-value">
                    {device.loadCurrent > 0 ? `${device.loadCurrent}A` : '-'}
                  </span>
                </td>
                <td>
                  <span className={`battery-status ${device.battery.mode.toLowerCase()}`}>
                    {getBatteryModeLabel(device.battery.mode)}
                  </span>
                </td>
                <td>
                  <span className={`rectifier-status ${device.rectifiers.online === device.rectifiers.total ? 'ok' :
                    device.rectifiers.online === 0 ? 'critical' : 'warning'
                    }`}>
                    {getRectifierStatus(device)}
                  </span>
                </td>
                <td>
                  <span className={`health-badge ${device.status.toLowerCase()}`}>
                    {getStatusLabel(device.status)}
                  </span>
                </td>
                <td className="actions-cell">
                  <button
                    className="action-btn detail"
                    onClick={(e) => handleViewDetails(e, device)}
                    title="View Details"
                  >
                    <FaInfoCircle />
                  </button>
                  <button
                    className="action-btn history"
                    onClick={(e) => handleViewHistory(e, device)}
                    title="View History"
                  >
                    <FaHistory />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SMPSDeviceTable;
